import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { Reducer } from 'redux';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { put, takeLatest, call } from 'redux-saga/effects';

import { TAppActions } from '../rootDuck';
import { ActionsUnion, createAction } from '../../utils/action-helper';
import { IServerResponse } from '../../interfaces/server';
import { getResponseMessage } from '../../utils/utils';
import { IBalanceResponse, IPostCancelData, IPostCompleteData } from '../../interfaces/balanceRequest';
import { getBalanceRequest, postCancelBalanceRequest, postCompleteBalanceRequest } from '../../crud/balanceRequest.crud';

// Action Types
const CLEAR_ME = 'balance/CLEAR_ME';
const FETCH_REQUEST = 'balance/FETCH_REQUEST';
const FETCH_SUCCESS = 'balance/FETCH_SUCCESS';
const FETCH_FAIL = 'balance/FETCH_FAIL';
const POST_COMPLETE = 'balance/POST_COMPLETE';
const POST_COMPLETE_SUCCESS = 'balance/POST_COMPLETE_SUCCESS';
const POST_COMPLETE_FAIL = 'balance/POST_COMPLETE_FAIL';
const POST_CANCEL = 'balance/POST_CANCEL';
const POST_CANCEL_SUCCESS = 'balance/POST_CANCEL_SUCCESS';
const POST_CANCEL_FAIL = 'balance/POST_CANCEL_FAIL';

// Initial State
export interface IInitialState {
  balanceData: IBalanceResponse | undefined;
  loading: boolean;
  success: boolean;
  error: string | null;
  postCompleteSuccessed: boolean;
  page: number;
  size: number;
  total: number;
}

const initialState: IInitialState = {
  balanceData: undefined,
  loading: false,
  success: false,
  error: null,
  postCompleteSuccessed: false,
  page: 0,
  size: 20,
  total: 0,
};

// Reducer
export const reducer: Reducer<IInitialState & PersistPartial, TAppActions> = persistReducer(
  { storage, key: 'balance', whitelist: ['balanceData'] },
  (state = initialState, action: any) => {
    switch (action.type) {
      case CLEAR_ME:
        return { ...state, loading: false, success: false };
      case FETCH_REQUEST:
        return { ...state, balanceData: undefined, loading: true, error: null, success: false };
      case FETCH_SUCCESS:
        return { ...state, page: action.payload.number, size: action.payload.size, total: action.payload.totalElements, balanceData: action.payload, loading: false, success: true };
      case FETCH_FAIL:
        return { ...state, loading: false, error: action.payload, success: false };
      case POST_COMPLETE:
        return { ...state, loading: true, error: null };
      case POST_COMPLETE_SUCCESS:
        return { ...state, postCompleteSuccessed: true, loading: false, error: null };
      case POST_COMPLETE_FAIL:
        return { ...state, postCompleteSuccessed: false, loading: false, error: null };
      case POST_CANCEL:
        return { ...state, loading: true, error: null };
      case POST_CANCEL_SUCCESS:
        return { ...state, loading: false, error: null };
      case POST_CANCEL_FAIL:
        return { ...state, loading: false, error: null };
      default:
        return state;
    }
  }
);

// Actions
export const actions = {
  clearMe: () => createAction(CLEAR_ME),
  fetchRequest: (payload: { page: number; size: number }) => createAction(FETCH_REQUEST, payload),
  fetchSuccess: (payload: IServerResponse<IBalanceResponse>) => createAction(FETCH_SUCCESS, payload),
  fetchFail: (payload: string) => createAction(FETCH_FAIL, payload),
  postCompleteRequest: (paymentId: string, data: IPostCompleteData) => createAction(POST_COMPLETE, { paymentId, data }),
  postCompleteSuccess: (payload: string) => createAction(POST_COMPLETE_SUCCESS, payload),
  postCompleteFail: (payload: string) => createAction(POST_COMPLETE_FAIL, payload),
  postCancelRequest: (paymentId: string, data: IPostCancelData) => createAction(POST_CANCEL, { paymentId, data }),
  postCancelSuccess: (payload: string) => createAction(POST_CANCEL_SUCCESS, payload),
  postCancelFail: (payload: string) => createAction(POST_CANCEL_FAIL, payload),
};

export type TActions = ActionsUnion<typeof actions>;

// Sagas
function* fetchSaga({ payload }: { payload: { page: number; size: number } }) {
  try {
    const { data }: { data: IServerResponse<IBalanceResponse> } = yield call(() => getBalanceRequest(payload.page, payload.size));
    yield put(actions.fetchSuccess(data));
  } catch (e) {
    yield put(actions.fetchFail(getResponseMessage(e)));
  }
}

function* postComplete({ payload }: { payload: { paymentId: string; data: IPostCompleteData } }) {
  try {
    const { data }: { data: IServerResponse<any> } = yield call(() => postCompleteBalanceRequest(payload.paymentId, payload.data));
    yield put(actions.postCompleteSuccess(data.data));
  } catch (e) {
    yield put(actions.postCompleteFail(getResponseMessage(e)));
  }
}

function* postCancel({ payload }: { payload: { paymentId: string; data: IPostCancelData } }) {
  try {
    const { data }: { data: IServerResponse<any> } = yield call(() => postCancelBalanceRequest(payload.paymentId, payload.data));
    yield put(actions.postCancelSuccess(data.data));
  } catch (e) {
    yield put(actions.postCancelFail(getResponseMessage(e)));
  }
}

export function* saga() {
  yield takeLatest<ReturnType<typeof actions.fetchRequest>>(FETCH_REQUEST, fetchSaga);
  yield takeLatest<ReturnType<typeof actions.postCompleteRequest>>(POST_COMPLETE, postComplete);
  yield takeLatest<ReturnType<typeof actions.postCancelRequest>>(POST_CANCEL, postCancel);
}